import { useEffect, useMemo, useRef, useState } from 'react';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { IconButton, Pagination, styled } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';

import { getAlertReport } from '../../../apis/deviceApi';

import ReportItem from './ReportItem';

const Container = styled('div')`
  flex: 1;
  display: flex;
  min-height: 0px;
  flex-direction: column;
  & .list {
    flex: 1;
    overflow: auto;
    min-height: 0px;
  }

  & .control {
    display: flex;
    align-items: center;
    & .operation-container {
      flex: 1;
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
  }
`;

interface AlertReportContentProps {
  fromDate: Date;
  toDate: Date;
  id: string;
  enableQuery: boolean;
  filterRegex?: RegExp;
}

const AlertReportContent: React.FC<AlertReportContentProps> = ({
  fromDate,
  toDate,
  id,
  enableQuery,
  filterRegex,
}) => {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [page, setPage] = useState(1);

  const { data, refetch, hasNextPage, fetchNextPage, isLoading, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-dasendpoint-general-reports', id, fromDate, toDate],
      queryFn: async ({ pageParam }: { pageParam?: string }) => {
        const res = await getAlertReport({
          id,
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
          nextCursor: pageParam,
          limit: 200,
        });
        return res.data;
      },
      getNextPageParam: (page) => page.paging.nextCursor,
      getPreviousPageParam: (page) => page.paging.prevCursor,
      enabled: enableQuery,
      initialPageParam: undefined,
    });

  const totalPage = useMemo(() => {
    if (data?.pages) {
      if (data.pages.length > 0 && data.pages[0].paging.nextCursor) {
        return Math.ceil(
          data.pages[0].metadata.total / data.pages[0].data.length,
        );
      } else {
        return 1;
      }
    }

    return 0;
  }, [data]);

  useEffect(() => {
    if (!data?.pages[page - 1] && hasNextPage) {
      fetchNextPage();
    }
  }, [page, data, hasNextPage]);

  const alertReportItems = (data?.pages[page - 1]?.data ?? []).map((d) => (
    <ReportItem
      key={`alert-report-${d.id}`}
      data={d}
      filterRegex={filterRegex}
    />
  ));

  return (
    <Container className="general-report-container">
      <div className="control">
        Total: {data?.pages[0]?.metadata?.total}
        <div className="operation-container">
          <IconButton onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
          <Pagination
            count={totalPage}
            page={page}
            onChange={(v, page) => {
              setPage(page);
            }}
          />
        </div>
      </div>
      <div
        ref={listRef}
        className="list"
        style={{ textAlign: isLoading || isFetching ? 'center' : undefined }}
      >
        {(enableQuery && isLoading) || isFetching
          ? 'Loading...'
          : alertReportItems.length > 0
            ? alertReportItems
            : 'No Data'}
      </div>
    </Container>
  );
};

export default AlertReportContent;
